import { useEffect } from 'react';

const POST_MESSAGE_SOURCE = 'jlive-embed';
const DEBOUNCE_MS = 100;

/**
 * When rendered inside a partner's iframe embed (`?embeddable=true`, see `useEmbeddable`), the page's
 * real content height varies with the number of events, expanded filters, etc. iframes never
 * auto-resize to their own content, and cross-origin security blocks the embedded page from resizing
 * its parent frame element directly — so instead this hook reports the current document height to the
 * parent window via `postMessage`, and the partner's own page-side script (not part of this repo)
 * resizes the iframe element accordingly.
 *
 * Observes `document.documentElement` with a `ResizeObserver` so any layout change (events loading,
 * filters expanding/collapsing, etc.) triggers a (debounced) re-post. Posts once immediately on mount
 * so the initial height is available without waiting for the first resize.
 */
const useEmbedPostHeight = (isEmbedded: boolean): void => {
  useEffect(() => {
    if (!isEmbedded || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    let debounceTimer: ReturnType<typeof setTimeout>;

    const postHeight = () => {
      window.parent.postMessage({ source: POST_MESSAGE_SOURCE, height: document.documentElement.scrollHeight }, '*');
    };

    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(postHeight, DEBOUNCE_MS);
    };

    postHeight();

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(document.documentElement);

    return () => {
      clearTimeout(debounceTimer);
      resizeObserver.disconnect();
    };
  }, [isEmbedded]);
};

export default useEmbedPostHeight;
